import { useEffect, useRef, useState } from 'react';
import SockJS from 'sockjs-client/dist/sockjs';
import { Client } from '@stomp/stompjs';
import { useDispatch, useSelector } from 'react-redux';

import callApi from '../service/callApi';
import { enqueueSnackbar } from '../redux/notifier/notifierActions';

const useConsumerProjectStatusTracker = ({ onChangeStatus, onInitialUpdate, selector }) => {
  const dispatch = useDispatch();

  const { projectName, projectPassword } = useSelector(selector);

  const [status, setStatus] = useState(null);

  const stompClientRef = useRef(null);
  const initialStateRef = useRef(null);

  const checkStatus = async () => {
    const response = await callApi.post(`/api/consumer/project/status/is/processing`, {
      projectName,
      projectPassword,
    });

    if (initialStateRef.current === null) {
      initialStateRef.current = response.data;
    }

    handleChangeStatus(response.data);

    return response.data;
  };

  const handleChangeStatus = (newStatus) => {
    setStatus(newStatus);

    if (onChangeStatus) {
      onChangeStatus(newStatus);
    }
  };

  const handleShowError = (frame) => {
    dispatch(
      enqueueSnackbar({
        message: 'cantConnectToProjectStratusTracker',
        options: {
          variant: 'error',
        },
      }),
    );

    console.error('Broker reported error: ' + frame.headers['message']);
    console.error('Additional details: ' + frame.body);
  };

  const handleClearInitialStatus = () => {
    initialStateRef.current = false;
  };

  const handleInitialUpdate = () => {
    if (onInitialUpdate) {
      onInitialUpdate();
    }

    handleClearInitialStatus();
  };

  useEffect(() => {
    checkStatus();

    const socket = new SockJS(`/ws/factive`);

    const stompClient = new Client({
      webSocketFactory: () => socket,

      onConnect: () => {
        stompClient.subscribe(`/project/${projectName}/${projectPassword}`, (response) => {
          const statusValue = JSON.parse(response.body);

          handleChangeStatus(statusValue);
        });
      },

      onStompError: handleShowError,
    });

    stompClient.activate();
    stompClientRef.current = stompClient;

    return () => {
      stompClient.deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName, projectPassword]);

  useEffect(() => {
    if (!status && initialStateRef.current) {
      handleInitialUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return {
    status,
    stompClientRef,
  };
};

export default useConsumerProjectStatusTracker;
